import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import DLOFilterByRoleModal, { RolesByType } from '../DLOFilterByRoleModal';
import DLOFilterByDayModal, { DayItem } from '../DLOFilterByDayModal';
import DLOMassUpdateModal from '../DLOMassUpdateModal';

export interface DLOCreateScheduleTableActionsProps {
  style?: ViewStyle;
  onAddEmployeeRow?: () => void;
  onSplitRole?: () => void;
  onDeleteRows?: () => void;
  onMassUpdate?: (hours: string) => void;
  onFilterByRole?: () => void;
  onFilterByDay?: () => void;
  onToggleHours?: (type: 'schedule' | 'actual') => void;
  // Current active tab from parent
  activeTab?: 'schedule' | 'actual';
  // Role data for the modal
  rolesByType?: RolesByType;
  onRoleFilterSave?: (selectedRoles: RolesByType) => void;
  // Day filter data for the modal
  dayFilterData?: DayItem[];
  onDayFilterSave?: (selectedDays: DayItem[]) => void;
  // Employee selection state
  hasSelectedEmployees?: boolean;
  selectedEmployeesCount?: number;
  canSplitRole?: boolean;
  // Selected days for enabling Mass Update
  selectedDays?: string[];
  // Loading state for disabling actions
  isAddEmployeeDisabled?: boolean;
  isSplitRoleDisabled?: boolean;
  isDeleteDisabled?: boolean;
  isMassUpdateDisabled?: boolean;
  isFilterByDayDisabled?: boolean;
  // Tab editability for week cutoff logic
  scheduledEditable?: boolean;
  actualEditable?: boolean;
}

const DLOCreateScheduleTableActions: React.FC<DLOCreateScheduleTableActionsProps> = ({
  style,
  onAddEmployeeRow,
  onSplitRole,
  onDeleteRows,
  onMassUpdate,
  onFilterByRole,
  onFilterByDay,
  onToggleHours,
  activeTab = 'schedule',
  rolesByType = { DSD: [], 'NON-DSD': [], Retail: [], Other: [] },
  onRoleFilterSave,
  dayFilterData = [
    { id: '0', name: 'Sunday', enabled: true, disabled: false },
    { id: '1', name: 'Monday', enabled: true, disabled: false },
    { id: '2', name: 'Tuesday', enabled: true, disabled: false },
    { id: '3', name: 'Wednesday', enabled: true, disabled: false },
    { id: '4', name: 'Thursday', enabled: true, disabled: false },
    { id: '5', name: 'Friday', enabled: true, disabled: false },
    { id: '6', name: 'Saturday', enabled: true, disabled: false },
  ],
  onDayFilterSave,
  hasSelectedEmployees = false,
  selectedEmployeesCount = 0,
  canSplitRole = false,
  selectedDays = [],
  isAddEmployeeDisabled = false,
  isSplitRoleDisabled = false,
  isDeleteDisabled = false,
  isMassUpdateDisabled = false,
  isFilterByDayDisabled = false,
  scheduledEditable = true,
  actualEditable = true,
}) => {
  const [isFilterByRoleHovered, setIsFilterByRoleHovered] = useState(false);
  const [isFilterByDayHovered, setIsFilterByDayHovered] = useState(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [isDayModalVisible, setIsDayModalVisible] = useState(false);
  const [isMassUpdateModalVisible, setIsMassUpdateModalVisible] = useState(false);

  // Mass Update should be enabled when both employees are selected AND days are selected AND not disabled
  const isMassUpdateEnabled = hasSelectedEmployees && selectedDays.length > 0 && !isMassUpdateDisabled;

  // Split Role should be enabled when can split AND not disabled  
  const isSplitRoleEnabled = canSplitRole && !isSplitRoleDisabled;  

  // Delete should be enabled when at least one employee row is selected AND not disabled.
  const isDeleteEnabled = hasSelectedEmployees && !isDeleteDisabled;

  const handleHoursToggle = (type: 'schedule' | 'actual') => {
    onToggleHours?.(type);
  };

  const handleFilterByRolePress = () => {
    setIsRoleModalVisible(true);
    onFilterByRole?.();
  };

  const handleRoleModalSave = (selectedRoles: RolesByType) => {
    onRoleFilterSave?.(selectedRoles);
    setIsRoleModalVisible(false);
  };

  const handleFilterByDayPress = () => {
    setIsDayModalVisible(true);
    onFilterByDay?.();
  };

  const handleDayModalSave = (selectedDays: DayItem[]) => {
    onDayFilterSave?.(selectedDays);
    setIsDayModalVisible(false);
  };

  const handleMassUpdatePress = () => {
    setIsMassUpdateModalVisible(true);
  };

  const handleMassUpdateSave = (hours: string) => {
    onMassUpdate?.(hours);
    setIsMassUpdateModalVisible(false);
  };

  const handleMassUpdateClose = () => {
    setIsMassUpdateModalVisible(false);
  };

  const PlusIcon = ({ disabled }: { disabled?: boolean }) => (
    <View style={styles.iconContainer}>
      <Text style={[styles.plusIcon, disabled && styles.plusIconDisabled]}>+</Text>
    </View>
  );

  const FilterIcon = ({ isHovered, disabled }: { isHovered?: boolean; disabled?: boolean }) => (
    <View style={styles.iconContainer}>
      <View style={styles.filterIcon}>
        <View style={[
          styles.filterLine, 
          { width: 16, height: 1.5 }, 
          isHovered && styles.filterLineHovered,
          disabled && styles.filterLineDisabled
        ]} />
        <View style={[
          styles.filterLine, 
          { width: 12, height: 1.5, marginTop: 3 }, 
          isHovered && styles.filterLineHovered,
          disabled && styles.filterLineDisabled
        ]} />
        <View style={[
          styles.filterLine, 
          { width: 8, height: 1.5, marginTop: 3 }, 
          isHovered && styles.filterLineHovered,
          disabled && styles.filterLineDisabled
        ]} />
      </View>
    </View>
  );

  return (
    <View style={[styles.container, style]}>
      {/* Left Action Buttons */}
      <View style={styles.leftSection}>
        {/* Add Employee Row - Primary Button (disabled during loading) */}
        <TouchableOpacity 
          style={isAddEmployeeDisabled ? styles.disabledPrimaryButton : styles.primaryButton} 
          onPress={onAddEmployeeRow}
          disabled={isAddEmployeeDisabled}
          activeOpacity={0.8}
        >
          <PlusIcon disabled={isAddEmployeeDisabled} />
          <Text style={isAddEmployeeDisabled ? styles.disabledPrimaryButtonText : styles.primaryButtonText}>Add Employee Row</Text>
        </TouchableOpacity>

        {/* Split Role - Conditionally Enabled/Disabled Button */}
        <TouchableOpacity 
          style={isSplitRoleEnabled ? styles.enabledSecondaryButton : styles.disabledSecondaryButton} 
          onPress={onSplitRole}
          disabled={!isSplitRoleEnabled}
          activeOpacity={0.8}
        >
          <Text style={isSplitRoleEnabled ? styles.enabledSecondaryButtonText : styles.disabledSecondaryButtonText}>
            Split Role
          </Text>
        </TouchableOpacity>

        {/* Delete - Conditionally Enabled/Disabled Button */}
        <TouchableOpacity
          style={isDeleteEnabled ? styles.enabledDeleteButton : styles.disabledSecondaryButton}
          onPress={onDeleteRows}
          disabled={!isDeleteEnabled}
          activeOpacity={0.8}
        >
          <Text style={isDeleteEnabled ? styles.enabledDeleteButtonText : styles.disabledSecondaryButtonText}>
            Delete Row(s)
          </Text>
        </TouchableOpacity>

        {/* Mass Update - Conditionally Enabled/Disabled Button */}
        <TouchableOpacity 
          style={isMassUpdateEnabled ? styles.enabledSecondaryButton : styles.disabledSecondaryButton} 
          onPress={handleMassUpdatePress}
          disabled={!isMassUpdateEnabled}
          activeOpacity={0.8}
        >
          <Text style={isMassUpdateEnabled ? styles.enabledSecondaryButtonText : styles.disabledSecondaryButtonText}>
            Mass Update
          </Text>
        </TouchableOpacity>

        {/* Filter by Role - Enabled Secondary Button with Hover Effect */}
        <TouchableOpacity 
          style={isFilterByRoleHovered ? styles.filterByRoleButtonHovered : styles.enabledSecondaryButton} 
          onPress={handleFilterByRolePress}
          onPressIn={() => setIsFilterByRoleHovered(true)}
          onPressOut={() => setIsFilterByRoleHovered(false)}
          activeOpacity={0.8}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => setIsFilterByRoleHovered(true),
            onMouseLeave: () => setIsFilterByRoleHovered(false),
          } as any)}
        >
          <FilterIcon isHovered={isFilterByRoleHovered} />
          <Text style={isFilterByRoleHovered ? styles.filterByRoleButtonTextHovered : styles.enabledSecondaryButtonText}>
            Filter by Role
          </Text>
        </TouchableOpacity>

        {/* Filter by Day - Secondary Button with Disabled State */}
        <TouchableOpacity
          style={isFilterByDayDisabled 
            ? styles.disabledSecondaryButton 
            : (isFilterByDayHovered ? styles.filterByDayButtonHovered : styles.enabledSecondaryButton)
          }
          onPress={isFilterByDayDisabled ? undefined : handleFilterByDayPress}
          disabled={isFilterByDayDisabled}
          onPressIn={() => !isFilterByDayDisabled && setIsFilterByDayHovered(true)}
          onPressOut={() => setIsFilterByDayHovered(false)}
          activeOpacity={isFilterByDayDisabled ? 1 : 0.8}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => !isFilterByDayDisabled && setIsFilterByDayHovered(true),
            onMouseLeave: () => setIsFilterByDayHovered(false),
          } as any)}
        >
          <FilterIcon isHovered={!isFilterByDayDisabled && isFilterByDayHovered} disabled={isFilterByDayDisabled} />
          <Text style={isFilterByDayDisabled 
            ? styles.disabledSecondaryButtonText 
            : (isFilterByDayHovered ? styles.filterByDayButtonTextHovered : styles.enabledSecondaryButtonText)
          }>
            Filter by Day
          </Text>
        </TouchableOpacity>
      </View>

      {/* Right Toggle Buttons */}
      <View style={styles.rightSection}>
        <View style={styles.toggleContainer}>
          {/* Schedule Hours Button - Always allow switching for reference */}
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonLeft,
              activeTab === 'schedule' && styles.toggleButtonActive,
            ]}
            onPress={() => handleHoursToggle('schedule')}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.toggleButtonText,
              activeTab === 'schedule' && styles.toggleButtonTextActive,
            ]}>
              Schedule Hours
            </Text>
          </TouchableOpacity>

          {/* Actual Hours Button */}
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonRight,
              activeTab === 'actual' && styles.toggleButtonActive,
              !actualEditable && activeTab !== 'actual' && styles.toggleButtonDisabled
            ]}
            onPress={() => handleHoursToggle('actual')}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.toggleButtonText,
              activeTab === 'actual' && styles.toggleButtonTextActive,
              !actualEditable && activeTab !== 'actual' && styles.toggleButtonTextDisabled
            ]}>
              Actual Hours
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Role Filter Modal */}
      <DLOFilterByRoleModal
        visible={isRoleModalVisible}
        onClose={() => setIsRoleModalVisible(false)}
        rolesByType={rolesByType}
        onSave={handleRoleModalSave}
      />

      {/* Day Filter Modal */}
      <DLOFilterByDayModal
        visible={isDayModalVisible}
        onClose={() => setIsDayModalVisible(false)}
        initialDays={dayFilterData}
        onApply={handleDayModalSave}
      />

      {/* Mass Update Modal */}
      <DLOMassUpdateModal
        visible={isMassUpdateModalVisible}
        onClose={handleMassUpdateClose}
        onSave={handleMassUpdateSave}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    height: 63,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    borderLeftColor: '#DFDFDF',
    borderRightColor: '#DFDFDF',
    borderLeftWidth: 1,
    borderRightWidth: 1,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  rightSection: {
    alignItems: 'flex-end',
  },
  // Primary Button Styles
  primaryButton: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    gap: 4,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Disabled Primary Button Styles
  disabledPrimaryButton: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#DFDFDF',
    borderRadius: 24,
    gap: 4,
  },
  disabledPrimaryButtonText: {
    color: '#A0A0A0',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Disabled Secondary Button Styles
  disabledSecondaryButton: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#FBFCFE',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#DFDFDF',
  },
  disabledSecondaryButtonText: {
    color: '#A0A0A0',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Enabled Secondary Button Styles
  enabledSecondaryButton: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#FBFCFE',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    gap: 4,
  },
  enabledSecondaryButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  enabledDeleteButton: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#FFF5F5',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#C62828',
    gap: 4,
  },
  enabledDeleteButtonText: {
    color: '#C62828',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Icon Styles
  iconContainer: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  plusIcon: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  plusIconDisabled: {
    color: '#A0A0A0',
  },
  filterIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterLine: {
    backgroundColor: '#005CBC',
  },
  filterLineHovered: {
    backgroundColor: '#FFFFFF',
  },
  filterLineDisabled: {
    backgroundColor: '#A0A0A0',
  },
  // Filter by Role Hover Styles
  filterByRoleButtonHovered: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    gap: 4,
  },
  filterByRoleButtonTextHovered: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Filter by Day Hover Styles
  filterByDayButtonHovered: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    gap: 4,
  },
  filterByDayButtonTextHovered: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  // Toggle Button Styles
  toggleContainer: {
    flexDirection: 'row',
    height: 40,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#D7E5F8',
    overflow: 'hidden',
  },
  toggleButton: {
    minWidth: 141,
    height: 40,
    paddingHorizontal: 8,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleButtonLeft: {
    backgroundColor: '#FBFCFE',
  },
  toggleButtonRight: {
    backgroundColor: '#FBFCFE',
  },
  toggleButtonActive: {
    backgroundColor: '#005CBC',
  },
  toggleButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
  },
  toggleButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  toggleButtonDisabled: {
    backgroundColor: '#F8F9FA',
    opacity: 0.6,
  },
  toggleButtonTextDisabled: {
    color: '#9CA3AF',
  },
});

export default DLOCreateScheduleTableActions;
